import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/auth-context'

function AuthNav() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [avatarTimestamp, setAvatarTimestamp] = useState(Date.now())
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Use the Auth Context instead of Redux
  const { user, profile, isLoading, signOut } = useAuth()

  // Update avatar timestamp when profile changes
  useEffect(() => {
    setAvatarTimestamp(Date.now())
  }, [profile?.avatar_url])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSignOut = async () => {
    try {
      // Use the signOut method from Auth Context
      await signOut()

      // Navigate to home page
      window.location.href = '/'
    } catch (_error) {
      void 0 // Intentionally ignored
      // Fallback: force refresh the page
      window.location.reload()
    }
  }

  // If still loading auth state, show a skeleton loader
  if (isLoading) {
    return (
      <div className="flex items-center space-x-4">
        <div className="w-8 h-8 rounded-full bg-gray-700 animate-pulse"></div>
        <div className="w-20 h-4 bg-gray-700 animate-pulse"></div>
      </div>
    )
  }

  // If user is not logged in, show login/signup links
  if (!user) {
    return (
      <div className="flex items-center space-x-4">
        <Link to="/login" className="text-text hover:text-primary transition-colors">
          Log in
        </Link>
        <Link
          to="/signup"
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
        >
          Sign up
        </Link>
      </div>
    )
  }

  // If user is logged in, show profile dropdown
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="flex items-center space-x-2"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-primary">
          <img
            src={
              profile?.avatar_url
                ? `${profile.avatar_url}${
                    profile.avatar_url.includes('?') ? '&' : '?'
                  }t=${avatarTimestamp}`
                : 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y'
            }
            alt="Profile"
            className="w-full h-full object-cover"
          />
        </div>
        <span className="text-text">{profile?.username || user.email?.split('@')[0]}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 text-text transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-background border border-gray-700 rounded-md shadow-lg z-10">
          <div className="py-1">
            <Link
              to="/account"
              className="block px-4 py-2 text-text hover:bg-gray-800 transition-colors"
              onClick={() => setIsDropdownOpen(false)}
            >
              Account
            </Link>
            <Link
              to="/settings"
              className="block px-4 py-2 text-text hover:bg-gray-800 transition-colors"
              onClick={() => setIsDropdownOpen(false)}
            >
              Settings
            </Link>
            <hr className="my-1 border-gray-700" />
            <button
              className="block w-full text-left px-4 py-2 text-red-500 hover:bg-gray-800 transition-colors"
              onClick={handleSignOut}
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export { AuthNav }
